import { useLocation } from 'react-router-dom';
import roundHalfEven from 'round-half-even';
import {
  ECOM_ORDER_PRODUCT_STATUS_ENUM,
  ECOM_ORDER_STATUS_ENUM,
  PAYMENT_TYPE_ENUM
} from '../config/constants';

const groupStoreOrderProductData = (productData, groupByRefundValue = false) =>
  productData
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .reduce((r, c) => {
      const existing = r.find(
        (p) =>
          p.uuid === c.uuid &&
          p.fkEcomOrderPackage === c.fkEcomOrderPackage &&
          p.addOns.length === c.addOns.length &&
          (!groupByRefundValue || p.refundValue === c.refundValue) &&
          p.addOns.every(
            (ao, i) =>
              ao.name === c.addOns[i].name && ao.value === c.addOns[i].value
          )
      );
      if (existing) {
        existing.quantity += c.quantity;
        existing.totalCost += c.totalCost;
        existing.taxTotal += c.taxTotal;
        existing.vendorCost += c.vendorCost;
        existing.serviceFees += c.serviceFees;
        existing.basePrice += c.basePrice;
        existing.amountRemaining += c.amountRemaining;
        existing.ecomOrderProductBulkTransfers.push(
          ...(c.ecomOrderProductBulkTransfers || [])
        );
        existing.ecomOrderShippingProducts.push(...c.ecomOrderShippingProducts);
        existing.shippedCount += c.shippedCount;
        existing.ecomOrderProducts.push({ ...c });
      }
      else {
        r.push({
          ...c,
          ecomOrderProducts: [{ ...c }],
          ecomOrderShippingProducts: [...c.ecomOrderShippingProducts],
          ecomOrderProductBulkTransfers: c.ecomOrderProductBulkTransfers
            ? [...c.ecomOrderProductBulkTransfers]
            : []
        });
      }
      return r;
    }, []);

const translateStoreOrderProductData = (
  lineItem,
  product,
  ecomOrder,
  ecomOrderReturnProducts = []
) => {
  const {
    returnedProducts = [],
    cancelledProducts = []
  } = ecomOrderReturnProducts
    .filter(
      (f) =>
        f.fkEcomOrderProduct ===
        (product.pkEcomOrderProduct ||
          product.ecomOrderProduct?.pkEcomOrderProduct)
    )
    .reduce(
      (r, c) => {
        if (c.isCancelReturn) {
          r.cancelledProducts.push(c);
        }
        else {
          r.returnedProducts.push(c);
        }
        return r;
      },
      { returnedProducts: [], cancelledProducts: [] }
    );

  const ecomOrderShippingProducts = product.ecomOrderShippingProducts.filter(
    (p) => !ecomOrder.isBulkShipment || p.fkEcomOrder === ecomOrder.pkEcomOrder
  );
  const shippedCount = ecomOrderShippingProducts.reduce(
    (r2, c2) => r2 + c2.count,
    0
  );
  const returnedCount = returnedProducts.reduce((r2, c2) => r2 + c2.count, 0);
  const cancelledCount = cancelledProducts.reduce((r2, c2) => r2 + c2.count, 0);
  const amountRemaining =
    product.count - shippedCount - returnedCount - cancelledCount;

  const addOnCost = lineItem
    ? lineItem.addOns.reduce((r, c) => r + c.unitAmountInCents, 0)
    : product.ecomOrderProductInputAnswers.reduce((r, c) => {
        if (c.adjustedCost) {
          return r + c.adjustedCost;
        }
        return r;
      }, 0);

  const reimbursedAmount =
    product.ecomOrderProductPayments
      ?.filter(
        (f) =>
          f.fkRegFormSubmissionPaymentType.toString() ===
          PAYMENT_TYPE_ENUM.REIMBURSEMENT.toString()
      )
      .reduce((r, c) => r + c.amount, 0) ?? 0;

  const unitCost = lineItem
    ? lineItem.totalInCents
    : product.ecomStoreProductVariant.price + addOnCost;
  const totalQuantity = lineItem ? lineItem.quantity : product.count;

  const ecomOrderPackage = product.fkEcomOrderPackage
    ? ecomOrder.ecomOrderPackages.find(
        (p) => p.pkEcomOrderPackage === product.fkEcomOrderPackage
      )
    : null;
  const ecomOrderPackageLineItem = ecomOrder.checkoutBalance?.lineItems.find(
    (i) => i.entityId === product.fkEcomOrderPackage && i.isBundleProduct
  );

  return {
    key:
      lineItem?.entityId ||
      product.pkEcomOrderProductBulk ||
      product.pkEcomOrderProduct,
    purchaseDate: ecomOrder.purchaseDate,
    pkEcomOrderProduct: product.pkEcomOrderProduct,
    pkEcomOrderProductBulk: product.pkEcomOrderProductBulk,
    fkEcomOrderPackage: product.fkEcomOrderPackage,
    ecomOrderPackage,
    ecomOrderPackageLineItem,
    fkEcomOrder: product.fkEcomOrder,
    ecomOrder,
    fkEcomOrderProductStatus: product.fkEcomOrderProductStatus,
    to: `/merchant/products/${
      lineItem?.uuid || product.ecomStoreProductVariant.ecomStoreProduct.uuid
    }`,
    previewImage:
      lineItem?.previewImage ||
      product.ecomStoreProductVariant.ecomVendorProductVariant.previewImage ||
      product.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .previewImage,
    isBulkShipment: product.isBulkShipment,
    name:
      lineItem?.productName ||
      product.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct.name,
    vendorName:
      product.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .vendorName,
    vendorCost:
      (product.ecomStoreProductVariant.ecomVendorProductVariant.price +
        product.vendorAddonPrice) *
      totalQuantity,
    sku: product.ecomStoreProductVariant.ecomVendorProductVariant.sku,
    barcode: product.ecomStoreProductVariant.ecomVendorProductVariant.barcode,
    uuid: product.ecomStoreProductVariant.ecomVendorProductVariant.uuid,
    productUuid: product.ecomStoreProductVariant.ecomStoreProduct.uuid,
    vendorProductUuid:
      product.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct.uuid,
    productUrlHandle:
      product.ecomStoreProductVariant.ecomStoreProduct.urlHandle,
    weight: product.ecomStoreProductVariant.ecomVendorProductVariant.weight,
    weightUnit:
      product.ecomStoreProductVariant.ecomVendorProductVariant.weightUnit,
    quantity: totalQuantity,
    bundleQuantity: lineItem?.bundleQuantity,
    unitCost,
    totalCost: unitCost * totalQuantity,
    taxTotal: (lineItem?.taxInCents || 0) * totalQuantity,
    unitTax: lineItem?.taxInCents || 0,
    taxRate: lineItem?.taxRate || 0,
    tax:
      lineItem?.tax.map((t) => ({
        ...t,
        taxInCents:
          roundHalfEven(
            ((unitCost - lineItem.taxInCents) * (t.taxRate / 100)) / 100
          ) * 100
      })) || [],
    addonServiceFees: product.addonServiceFees * totalQuantity,
    serviceFees:
      (product.serviceFees + product.addonServiceFees) * totalQuantity,
    basePrice: (product.baseStorePrice + product.addonPrice) * totalQuantity,
    storeProfit: product.storeProfit,
    totalStoreProfit: product.storeProfit * totalQuantity,
    refundValue: product.refundValue - product.storeProfit,
    requireExactMinimum:
      product.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .requireExactMinimum,
    minimumQuantity:
      product.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .minimumQuantity,
    amountRemaining,
    ecomOrderShippingProducts,
    shippedCount,
    returnedProducts,
    cancelledProducts,
    returnedCount,
    cancelledCount,
    reimbursedAmount,
    ecomOrderProducts: [],
    ecomOrderProduct: product,
    ecomOrderProductBulkTransfers: product.ecomOrderProductBulkTransfers,
    ecomCustomerAddressShipping: product.ecomCustomerAddressShipping,
    addOns: lineItem?.addOns
      .map((a) => ({
        name: a.name,
        value: a.value,
        sort: a.sort,
        isVariantOption: a.isVariantOption
      }))
      .sort((a, b) => a.sort - b.sort) || [
      ...product.ecomStoreProductVariant.ecomVendorProductVariant.ecomVendorProductVariantOptions.map(
        (op) => ({
          name: op.name,
          value: op.value,
          sort: op.nameSort,
          isVariantOption: true
        })
      ),
      ...product.ecomOrderProductInputAnswers
        .map((a) => ({
          name: a.label,
          value: a.value,
          sort: a.sort,
          isInputAnswer: true
        }))
        .sort(
          (a, b) =>
            a.sort -
            b.sort +
            product.ecomStoreProductVariant.ecomVendorProductVariant
              .ecomVendorProductVariantOptions.length
        )
    ],
    ecomOrderProductBulk:
      !ecomOrder.isBulkShipment && product.isBulkShipment
        ? product.ecomOrderProductBulk.map((b) => ({
            pkEcomOrderProductBulk: b.pkEcomOrderProductBulk,
            count: b.count,
            createdAt: b.createdAt,
            fkEcomOrderProductStatus: b.fkEcomOrderProductStatus,
            fkEcomOrderStatus: b.fkEcomOrderStatus,
            fkEcomOrder: b.fkEcomOrder,
            to: `/merchant/group-orders/${b.fkEcomOrder}`
          }))
        : null,
    ecomStoreRosterPlayer:
      lineItem === null
        ? product.ecomStoreRosterPlayer
        : ecomOrder.ecomStoreRosterPlayer,
    ecomCustomer: product.ecomCustomer
  };
};

const translateStoreOrderData = (ecomOrder) => {
  const ecomOrderReturnProducts =
    ecomOrder?.ecomOrderReturns.reduce(
      (r, c) => [
        ...r,
        ...c.ecomOrderReturnProducts.map((p) => ({
          ...p,
          isCancelReturn: c.isCancelReturn
        }))
      ],
      []
    ) ?? [];

  const lineItems = ecomOrder
    ? [
        ...ecomOrder.checkoutBalance.lineItems.filter(
          (i) => !i.isBundleProduct
        ),
        ...ecomOrder.checkoutBalance.lineItems.flatMap((i) => i.bundleItems)
      ]
    : [];

  const translatedProductData = ecomOrder
    ? [
        ...lineItems.map((p) =>
          translateStoreOrderProductData(
            p,
            ecomOrder.ecomOrderProducts.find(
              (c) => c.pkEcomOrderProduct === p.entityId
            ),
            ecomOrder,
            ecomOrderReturnProducts
          )
        ),
        ...ecomOrder.ecomOrderProductBulk
          .filter(
            (b) =>
              ![
                ECOM_ORDER_PRODUCT_STATUS_ENUM.TRANSFERRED,
                ECOM_ORDER_PRODUCT_STATUS_ENUM.CANCELLED,
                ECOM_ORDER_PRODUCT_STATUS_ENUM.REFUNDED
              ].includes(b.fkEcomOrderProductStatus.toString())
          )
          .map((b) =>
            translateStoreOrderProductData(
              null,
              {
                ...b.ecomOrderProduct,
                ...b,
                fkEcomOrder: b.ecomOrderProduct.fkEcomOrder
              },
              ecomOrder,
              ecomOrderReturnProducts
            )
          )
      ]
    : [];

  const productData = groupStoreOrderProductData(translatedProductData);

  const productDataGroupedByPackage = productData
    .filter((p) => p.quantity - p.cancelledCount > 0)
    .reduce((r, c) => {
      const existing = r.find(
        (f) => f.ecomOrderPackage?.pkEcomOrderPackage === c.fkEcomOrderPackage
      );
      if (existing) {
        existing.products.push(c);
      }
      else {
        r.push({
          key: c.key,
          ecomOrderPackage: c.ecomOrderPackage,
          ecomOrderPackageLineItem: c.ecomOrderPackageLineItem,
          products: [c]
        });
      }

      return r;
    }, []);

  return {
    ecomOrderShippingProducts: productData
      .map((p) => p.ecomOrderShippingProducts)
      .flat(),
    ecomOrderProducts: productData.sort((a, b) => a.name.localeCompare(b.name)),
    productDataGroupedByPackage,
    ecomOrderProductsOriginal: translatedProductData,
    groupedEcomOrderProducts: [...productData]
      .reduce((r, c) => {
        const existing = r.find(
          (f) => f.vendorProductUuid === c.vendorProductUuid
        );
        if (existing) {
          existing.quantity += c.quantity;
          existing.totalCost += c.totalCost;
          existing.taxTotal += c.taxTotal;
          existing.vendorCost += c.vendorCost;
          existing.serviceFees += c.serviceFees;
          existing.basePrice += c.basePrice;
          existing.amountRemaining += c.amountRemaining;
          existing.ecomOrderShippingProducts.push(
            ...c.ecomOrderShippingProducts
          );
          existing.shippedCount += c.shippedCount;
          existing.minimumAmountRequiredRemaining = Math.max(
            0,
            existing.minimumAmountRequiredRemaining - c.quantity
          );
          existing.ecomOrderProducts.push(c);

          if (c.ecomOrderProductBulkTransfer) {
            existing.ecomOrderProductBulkTransfers.push(
              ...c.ecomOrderProductBulkTransfers
            );
          }
        }
        else {
          r.push({
            ...c,
            addOns: [],
            ecomOrderProducts: [],
            ecomOrderProductBulkTransfers: c.ecomOrderProductBulkTransfer
              ? [...c.ecomOrderProductBulkTransfers]
              : [],
            ecomOrderShippingProducts: [...c.ecomOrderShippingProducts]
          });
        }
        return r;
      }, [])
      .map((c) => {
        const minimumAmountRequired = c.requireExactMinimum
          ? Math.ceil(c.quantity / c.minimumQuantity) * c.minimumQuantity
          : c.minimumQuantity;
        return {
          ...c,
          minimumAmountReachedCount: Math.floor(c.quantity / c.minimumQuantity),
          minimumAmountRequired,
          minimumAmountRequiredRemaining: Math.max(
            0,
            minimumAmountRequired - c.quantity
          )
        };
      }),
    ecomOrderProductBulkChanges: ecomOrder
      ? groupStoreOrderProductData([
          ...ecomOrder.ecomOrderProductBulk
            .filter((b) =>
              [
                ECOM_ORDER_PRODUCT_STATUS_ENUM.TRANSFERRED,
                ECOM_ORDER_PRODUCT_STATUS_ENUM.CANCELLED,
                ECOM_ORDER_PRODUCT_STATUS_ENUM.REFUNDED
              ].includes(b.fkEcomOrderProductStatus.toString())
            )
            .map((b) =>
              translateStoreOrderProductData(
                null,
                {
                  ...b.ecomOrderProduct,
                  ...b,
                  fkEcomOrder: b.ecomOrderProduct.fkEcomOrder
                },
                ecomOrder,
                ecomOrderReturnProducts
              )
            )
        ]).reduce(
          (r, c) => {
            switch (c.fkEcomOrderProductStatus.toString()) {
              case ECOM_ORDER_PRODUCT_STATUS_ENUM.CANCELLED:
              case ECOM_ORDER_PRODUCT_STATUS_ENUM.REFUNDED:
                r.cancelled.push(c);
                break;
              case ECOM_ORDER_PRODUCT_STATUS_ENUM.TRANSFERRED:
                r.transferred.push(c);
                break;
              default:
                break;
            }
            return r;
          },
          {
            cancelled: [],
            transferred: []
          }
        )
      : {
          cancelled: [],
          transferred: []
        },
    ecomCustomers: translatedProductData.reduce((r, c) => {
      const customer = c.ecomCustomer || ecomOrder.ecomCustomer;

      const existing = r.find(
        (f) => f.pkEcomCustomer === customer.pkEcomCustomer
      );
      if (!existing) {
        const products = groupStoreOrderProductData(
          translatedProductData.filter(
            (p) => p.ecomCustomer?.pkEcomCustomer === customer.pkEcomCustomer
          )
        );

        const allOrderProducts = products
          .map((p) => p.ecomOrderProducts)
          .flat();

        r.push({
          ...customer,
          ecomCustomerAddressShipping: products.filter(
            (f) => !!f.ecomCustomerAddressShipping
          )[0]?.ecomCustomerAddressShipping,
          products,
          fundraiserCustomInputs: [
            ...new Set(
              allOrderProducts
                .map((p) => p.ecomOrderProduct?.fundraiserCustomInput)
                .filter((f) => !!f)
            )
          ],
          ecomStoreRosterPlayers: allOrderProducts.reduce((r1, c1) => {
            if (
              !!c1.ecomStoreRosterPlayer &&
              !r1.find(
                (f) =>
                  f.pkEcomStoreRosterPlayer ===
                  c1.ecomStoreRosterPlayer.pkEcomStoreRosterPlayer
              )
            ) {
              r1.push(c1.ecomStoreRosterPlayer);
            }
            return r1;
          }, [])
        });
      }

      return r;
    }, [])
  };
};

const calculateStoreOrderRefundValues = (
  ecomOrder,
  productData,
  selectedEcomOrderProducts,
  unlockRefundAmount = false
) => {
  // Summarize selected products
  const selectedProductsSummary = ecomOrder
    ? selectedEcomOrderProducts.reduce(
        (summary, selectedProduct) => {
          const product = productData.find(
            (p) => p.pkEcomOrderProduct === selectedProduct.pkEcomOrderProduct
          );

          const count = Math.min(
            selectedProduct.count || 0,
            product.amountRemaining
          );

          return {
            unitCost: summary.unitCost + count * product.unitCost,
            vendorCost: summary.vendorCost + count * product.vendorCost,
            itemCount: summary.itemCount + count,
            refundValue: summary.refundValue + count * product.refundValue,
            taxAmount:
              summary.taxAmount +
              count *
                (roundHalfEven(
                  ((product.unitCost - product.unitTax) * product.taxRate) / 100
                ) *
                  100),
            storeProfit: summary.storeProfit + count * product.storeProfit,
            taxRates:
              count > 0
                ? [...new Set([...summary.taxRates, product.taxRate])]
                : summary.taxRates
          };
        },
        {
          unitCost: 0,
          vendorCost: 0,
          itemCount: 0,
          refundValue: 0,
          taxAmount: 0,
          storeProfit: 0,
          taxRates: []
        }
      )
    : {};

  // Check if all items are refunded
  const totalItemCount = productData.reduce((acc, p) => acc + p.quantity, 0);
  const hasRefundedAllItems =
    selectedProductsSummary.itemCount >= totalItemCount;

  const { platformFeeAmount = 0, reimbursedAmount = 0 } =
    ecomOrder?.ecomOrderPayments.reduce(
      (r, c) => ({
        reimbursedAmount:
          c.fkRegFormSubmissionPaymentType.toString() ===
          PAYMENT_TYPE_ENUM.REIMBURSEMENT.toString()
            ? r.reimbursedAmount + c.amount
            : r.reimbursedAmount,
        platformFeeAmount:
          r.platformFeeAmount +
          (c.ecomStoreCartPaymentFee?.applicationFeeAmount || 0)
      }),
      { platformFeeAmount: 0, reimbursedAmount: 0 }
    ) || {};

  // Destructure fees from the checkout balance
  const {
    serviceFeeRecordedInCents: recordedServiceFee = 0,
    baseServiceFeeInCents: expectedServiceFee = 0,
    tieredPricingFeeInCents = 0,
    amountPaidInCents = 0,
    refundedAmountInCents = 0,
    taxTotalInCents = 0,
    shippingTotalInCents = 0
  } = ecomOrder?.checkoutBalance || {};

  // Calculate fee adjustments
  const expectedVsRecordedServiceFeeDifference =
    recordedServiceFee - expectedServiceFee;

  // Calculate fulfillment costs
  const totalProductFulfillmentCost = productData.reduce(
    (total, product) => total + product.refundValue * product.quantity,
    0
  );

  // Calculate tax
  const totalProductTax = productData.reduce((r, c) => r + c.taxTotal, 0);
  const tiedUpProductCost =
    totalProductFulfillmentCost - selectedProductsSummary.refundValue;
  const remainingTieredPricing =
    platformFeeAmount - totalProductFulfillmentCost;

  // Adjust for Stripe fee rounding
  const stripeFeeRoundingAdjustment = ecomOrder?.isBulkShipment
    ? 0
    : totalProductFulfillmentCost -
      (platformFeeAmount - tieredPricingFeeInCents - shippingTotalInCents);
  const tieredPricing = ecomOrder?.isBulkShipment
    ? 0
    : tieredPricingFeeInCents - stripeFeeRoundingAdjustment;

  // Calculate total store profit
  const totalStoreProfit =
    amountPaidInCents -
    recordedServiceFee -
    totalProductFulfillmentCost -
    tieredPricing -
    shippingTotalInCents -
    taxTotalInCents;

  // Calculate refund adjustments
  const tieredPricingReducer =
    selectedProductsSummary.itemCount > 0 && hasRefundedAllItems
      ? Math.min(tieredPricing, 0)
      : Math.min(tieredPricing, remainingTieredPricing);
  const refundAmountReducer =
    recordedServiceFee +
    tiedUpProductCost +
    tieredPricingReducer +
    shippingTotalInCents;

  const maxRefundAmount =
    amountPaidInCents - refundAmountReducer + reimbursedAmount;
  const maxRefundAmountWithoutLosses = maxRefundAmount - refundedAmountInCents;

  // Calculate available refund amount
  const availableRefundAmount =
    (unlockRefundAmount ? amountPaidInCents : maxRefundAmount) -
    refundedAmountInCents;

  return {
    selectedItemCount: selectedProductsSummary.itemCount,
    selectedItemsTotalUnitCost: selectedProductsSummary.unitCost,
    selectedItemsStoreProfit: selectedProductsSummary.storeProfit,
    selectedItemsTaxAmount: selectedProductsSummary.taxAmount,
    selectedItemsRefundValue: selectedProductsSummary.refundValue,
    selectedItemsTaxRates: selectedProductsSummary.taxRates,
    productTaxRates: productData.reduce(
      (r, c) => [...new Set([...r, c.taxRate])],
      []
    ),
    amountPaid: amountPaidInCents,
    totalProductFulfillmentCost,
    hasRefundedAllItems,
    totalStoreProfit,
    totalTaxAmount: taxTotalInCents ?? totalProductTax,
    serviceFeePaid: recordedServiceFee,
    tieredPricing,
    tiedUpProductCost,
    availableRefundAmount,
    refundedAmountInCents,
    reimbursedAmount,
    shippingTotalInCents,
    maxRefundAmountWithoutLosses,
    expectedVsRecordedServiceFeeDifference
  };
};

const useStoreBackPath = (defaultBack = '/merchant', { overrideBack } = {}) => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const fromLocations = searchParams.get('from')?.split(',') || [];
  const fromId = searchParams.get('fromId');

  const getBackPath = () => {
    const routes = {
      analytics: `/merchant/analytics`,
      orders: `/merchant/orders`,
      'group-orders': `/merchant/group-orders`,
      menu: `/merchant/menus`
    };

    const params =
      fromLocations.length > 1
        ? `?from=${fromLocations.slice(1).join(',')}`
        : ``;
    const path = routes[fromLocations[0]?.toLocaleLowerCase()];
    return overrideBack
      ? `${overrideBack}${params}`
      : path
      ? `${path}${fromId ? `/${fromId}` : ''}${params}`
      : defaultBack;
  };

  return { backPath: getBackPath() };
};

const getOrderSummaryInfo = (d, isBulkOrders) => {
  const totals = d.ecomOrderPayments.reduce(
    (acc, p) => {
      switch (p.fkRegFormSubmissionPaymentType.toString()) {
        case PAYMENT_TYPE_ENUM.REFUND:
          acc.refunded += p.amount;
          break;
        case PAYMENT_TYPE_ENUM.REIMBURSEMENT:
          acc.reimbursed += p.amount;
          break;
        default:
          acc.paid += p.amount;
      }
      return acc;
    },
    { refunded: 0, reimbursed: 0, paid: 0 }
  );

  const completelyRefunded = totals.paid > 0 && totals.refunded >= totals.paid;

  const bulkProducts = d.ecomOrderProductBulk.filter(
    (b) =>
      ![
        ECOM_ORDER_PRODUCT_STATUS_ENUM.TRANSFERRED,
        ECOM_ORDER_PRODUCT_STATUS_ENUM.CANCELLED,
        ECOM_ORDER_PRODUCT_STATUS_ENUM.REFUNDED
      ].includes(b.fkEcomOrderProductStatus.toString())
  );

  const bulkInfo = bulkProducts.reduce(
    (acc, p) => {
      acc.itemCount += p.count;
      acc.cancelledCount += p.cancelledCount;
      acc.isFullyFulfilled =
        acc.isFullyFulfilled &&
        ![
          ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING,
          ECOM_ORDER_PRODUCT_STATUS_ENUM.ACCEPTED
        ].includes(p.fkEcomOrderProductStatus.toString());
      return acc;
    },
    { itemCount: 0, cancelledCount: 0, isFullyFulfilled: true }
  );

  const productInfo = d.ecomOrderProducts.reduce(
    (acc, p) => {
      acc.itemCount += p.count;
      acc.cancelledCount += p.cancelledCount;
      if (!p.isBulkShipment) {
        acc.isFullyFulfilled =
          acc.isFullyFulfilled &&
          ![
            ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING,
            ECOM_ORDER_PRODUCT_STATUS_ENUM.ACCEPTED
          ].includes(p.fkEcomOrderProductStatus.toString());
      }

      p.ecomVendorProductExternalIds.forEach((id) => {
        if (
          !acc.externalConnectionTypes.includes(id.fkEcomExternalConnectionType)
        ) {
          acc.externalConnectionTypes.push(id.fkEcomExternalConnectionType);
        }
      });

      return acc;
    },
    {
      itemCount: 0,
      cancelledCount: 0,
      isFullyFulfilled: true,
      externalConnectionTypes: []
    }
  );

  const isFullyCancelled =
    !isBulkOrders && productInfo.cancelledCount === productInfo.itemCount;

  const totalPaid = isBulkOrders
    ? bulkProducts.reduce(
        (sum, p) => sum + p.ecomOrderProduct.productCost * p.count,
        0
      )
    : d.ecomOrderPayments
        .filter(
          (p) =>
            ![
              PAYMENT_TYPE_ENUM.REFUND,
              PAYMENT_TYPE_ENUM.REIMBURSEMENT
            ].includes(p.fkRegFormSubmissionPaymentType.toString())
        )
        .reduce((sum, p) => sum + p.amount, 0);

  const minimumQuantity = isBulkOrders
    ? bulkProducts.reduce(
        (acc, p) => {
          if (!acc.uuids.has(p.ecomOrderProduct.vendorProductUuid)) {
            acc.uuids.add(p.ecomOrderProduct.vendorProductUuid);
            acc.total += p.ecomOrderProduct.minimumQuantity;
          }
          return acc;
        },
        { uuids: new Set(), total: 0 }
      ).total
    : 0;

  const statusInfo =
    d.fkEcomOrderStatus.toString() === ECOM_ORDER_STATUS_ENUM.COMPLETED_OFFLINE
      ? bulkInfo.itemCount === 0
        ? { color: 'green', label: 'Transferred' }
        : { color: 'green', label: 'Completed Offline' }
      : d.fkEcomOrderStatus.toString() === ECOM_ORDER_STATUS_ENUM.ACCUMULATING
      ? {
          color: 'blue',
          label: 'Accumulating'
        }
      : isFullyCancelled
      ? {
          color: '#C40000',
          label: 'Cancelled'
        }
      : productInfo.isFullyFulfilled && bulkInfo.isFullyFulfilled
      ? {
          color: 'green',
          label: 'Fulfilled'
        }
      : {
          color: 'orange',
          label: 'Unfulfilled'
        };

  const paymentStatusInfo = completelyRefunded
    ? {
        color: '#C40000',
        label: 'Refunded'
      }
    : totals.refunded > 0
    ? {
        color: '#666',
        label: 'Partially refunded'
      }
    : {
        color: 'dodgerblue',
        label: 'Paid'
      };

  return {
    totals,
    completelyRefunded,
    bulkProducts,
    bulkInfo,
    productInfo,
    isFullyCancelled,
    totalPaid,
    minimumQuantity,
    statusInfo,
    paymentStatusInfo
  };
};

const calculateStoreOrderData = (ecomOrder) => {
  const translatedOrder = translateStoreOrderData(ecomOrder);
  const { ecomOrderPRoducts: productData } = translatedOrder;

  const isCompletedOffline =
    ecomOrder?.fkEcomOrderStatus.toString() ===
    ECOM_ORDER_STATUS_ENUM.COMPLETED_OFFLINE.toString();

  const isAccumulating =
    ecomOrder?.fkEcomOrderStatus.toString() ===
    ECOM_ORDER_STATUS_ENUM.ACCUMULATING.toString();
  ecomOrder?.fkEcomOrderStatus.toString() ===
    ECOM_ORDER_STATUS_ENUM.ACCUMULATING.toString();

  const isPendingFulfillment = ecomOrder?.isBulkShipment
    ? isAccumulating
    : productData.some(
        (p) =>
          p.fkEcomOrderProductStatus.toString() ===
          ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING
      );

  return {
    ...translatedOrder,
    isCompletedOffline,
    isAccumulating,
    isPendingFulfillment
  };
};

const getBundleTaxItems = (lineItem) => {
  if (lineItem.taxInCents === 0 || !lineItem.bundleItems) {
    return [];
  }

  const bundleItemsWithTax = lineItem.bundleItems.filter(
    (bi) => bi.tax.length > 0
  );

  const taxNames = new Set(
    lineItem.bundleItems.flatMap((bi) => bi.tax.map((t) => t.name))
  );

  const taxRates = new Set(
    lineItem.bundleItems.flatMap((bi) => bi.tax.map((t) => t.taxRate))
  );

  const commonTax =
    taxNames.size === 1 && bundleItemsWithTax.length > 0
      ? bundleItemsWithTax[0].tax[0]
      : null;

  const bundleTaxTotal = lineItem.bundleItems.reduce(
    (acc, bi) => acc + bi.taxInCents * bi.quantity,
    0
  );

  return [
    {
      entityId: lineItem.entityId,
      tax: [
        {
          name: commonTax?.name ?? null,
          taxRate: taxRates.size === 1 ? commonTax?.taxRate ?? null : null,
          taxAmount: lineItem.taxInCents * lineItem.quantity - bundleTaxTotal
        }
      ]
    },
    ...bundleItemsWithTax.map((bi) => ({
      entityId: bi.entityId,
      tax: bi.tax.map((t) => ({
        name: t.name,
        taxRate: t.taxRate,
        taxAmount:
          roundHalfEven((bi.subtotalInCents * t.taxRate) / 100, 0) * bi.quantity
      }))
    }))
  ];
};

export {
  translateStoreOrderData,
  translateStoreOrderProductData,
  calculateStoreOrderData,
  calculateStoreOrderRefundValues,
  useStoreBackPath,
  getOrderSummaryInfo,
  getBundleTaxItems
};
